import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Plus, Trash2, Users, Layers, ChevronDown, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EventDivisionsManager = ({ eventId, token, onUpdate }) => {
  const [divisions, setDivisions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [newDivName, setNewDivName] = useState('');
  const [expandedDiv, setExpandedDiv] = useState(null);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamColor, setNewTeamColor] = useState('#0066CC');
  const [loading, setLoading] = useState(true);

  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [divRes, teamRes] = await Promise.all([
        fetch(`${API_URL}/api/events/${eventId}/divisions`),
        fetch(`${API_URL}/api/events/${eventId}/teams`),
      ]);
      if (divRes.ok) setDivisions(await divRes.json());
      if (teamRes.ok) setTeams(await teamRes.json());
    } catch {}
    setLoading(false);
  }, [eventId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addDivision = async () => {
    if (!newDivName.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/divisions`, {
        method: 'POST', headers, body: JSON.stringify({ name: newDivName.trim() })
      });
      if (res.ok) {
        toast.success(`Division "${newDivName}" added`);
        setNewDivName('');
        fetchData();
        onUpdate?.();
      } else {
        toast.error('Failed to add division');
      }
    } catch { toast.error('Error adding division'); }
  };

  const removeDivision = async (divId, divName) => {
    if (!window.confirm(`Remove "${divName}" and all its teams?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/divisions/${divId}`, {
        method: 'DELETE', headers
      });
      if (res.ok) {
        toast.success(`Division "${divName}" removed`);
        fetchData();
        onUpdate?.();
      }
    } catch { toast.error('Error removing division'); }
  };

  const addTeam = async (divId) => {
    if (!newTeamName.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/teams`, {
        method: 'POST', headers,
        body: JSON.stringify({ name: newTeamName.trim(), division_id: divId, color: newTeamColor })
      });
      if (res.ok) {
        toast.success(`Team "${newTeamName}" added`);
        setNewTeamName('');
        setNewTeamColor('#0066CC');
        fetchData();
        onUpdate?.();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to add team');
      }
    } catch { toast.error('Error adding team'); }
  };

  const removeTeam = async (teamId, teamName) => {
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/teams/${teamId}`, {
        method: 'DELETE', headers
      });
      if (res.ok) {
        toast.success(`Team "${teamName}" removed`);
        fetchData();
        onUpdate?.();
      }
    } catch { toast.error('Error removing team'); }
  };

  if (loading) {
    return <div className="text-center py-8 text-zinc-500 text-sm">Loading...</div>;
  }

  return (
    <div className="space-y-4 py-2" data-testid="divisions-manager">
      {/* Add Division */}
      <div>
        <Label className="text-zinc-300 text-xs uppercase tracking-wider">Add Division</Label>
        <div className="flex gap-2 mt-1">
          <Input
            value={newDivName}
            onChange={(e) => setNewDivName(e.target.value)}
            placeholder="e.g., 15U, 16U, 17U"
            className="bg-zinc-900 border-zinc-700 text-white text-sm"
            onKeyDown={(e) => e.key === 'Enter' && addDivision()}
            data-testid="add-division-input"
          />
          <Button onClick={addDivision} size="sm" className="bg-emerald-600 hover:bg-emerald-500 shrink-0" data-testid="add-division-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add
          </Button>
        </div>
      </div>

      {/* Division List */}
      {divisions.length === 0 ? (
        <div className="text-center py-6 text-zinc-600 text-sm">
          No divisions yet. Add your first division above.
        </div>
      ) : (
        <div className="space-y-2">
          {divisions.map((div) => {
            const divTeams = teams.filter(t => t.division_id === div.id);
            const isExpanded = expandedDiv === div.id;

            return (
              <div key={div.id} className="bg-zinc-900 rounded-lg border border-zinc-800 overflow-hidden" data-testid={`division-${div.id}`}>
                {/* Division Header */}
                <button
                  onClick={() => setExpandedDiv(isExpanded ? null : div.id)}
                  className="w-full flex items-center justify-between p-3 hover:bg-zinc-800/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    {isExpanded ? <ChevronDown className="h-4 w-4 text-zinc-500" /> : <ChevronRight className="h-4 w-4 text-zinc-500" />}
                    <Layers className="h-4 w-4 text-emerald-400" />
                    <span className="text-sm font-medium text-white">{div.name}</span>
                    <Badge className="bg-zinc-800 text-zinc-400 text-[10px]">{divTeams.length} teams</Badge>
                    {div.game_count > 0 && (
                      <Badge className="bg-cyan-500/15 text-cyan-400 text-[10px]">{div.game_count} games</Badge>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-red-500 hover:bg-red-500/10"
                    onClick={(e) => { e.stopPropagation(); removeDivision(div.id, div.name); }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </button>

                {/* Teams List */}
                {isExpanded && (
                  <div className="border-t border-zinc-800 p-3 space-y-2">
                    {divTeams.map((team) => (
                      <div key={team.id} className="flex items-center justify-between py-1.5 px-2 rounded bg-zinc-800/50" data-testid={`team-${team.id}`}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: team.color || '#666' }} />
                          <span className="text-sm text-zinc-300">{team.name}</span>
                          {team.coach_name && <span className="text-[10px] text-zinc-600">({team.coach_name})</span>}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-1.5 text-red-500 hover:bg-red-500/10"
                          onClick={() => removeTeam(team.id, team.name)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}

                    {/* Add Team */}
                    <div className="flex gap-2 mt-2 pt-2 border-t border-zinc-800">
                      <div className="flex items-center gap-1.5 flex-1">
                        <input
                          type="color"
                          value={newTeamColor}
                          onChange={(e) => setNewTeamColor(e.target.value)}
                          className="w-7 h-7 rounded cursor-pointer bg-transparent border-0"
                        />
                        <Input
                          value={newTeamName}
                          onChange={(e) => setNewTeamName(e.target.value)}
                          placeholder="Team name"
                          className="bg-zinc-800 border-zinc-700 text-white text-sm h-8"
                          onKeyDown={(e) => e.key === 'Enter' && addTeam(div.id)}
                          data-testid={`add-team-input-${div.id}`}
                        />
                      </div>
                      <Button
                        onClick={() => addTeam(div.id)}
                        size="sm"
                        className="bg-zinc-700 hover:bg-zinc-600 h-8 shrink-0"
                        data-testid={`add-team-btn-${div.id}`}
                      >
                        <Plus className="h-3 w-3 mr-1" /> Team
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EventDivisionsManager;
